import * as React from 'react';
import { useRef, useCallback } from 'react';
import { Textarea } from './textarea';
import { Button } from './button';
import { cn } from '../../lib/utils';

// Available contact variables for template body
const TEMPLATE_VARIABLES = [
  { name: 'Nome', description: 'Nome do contato', sample: 'Joao Silva' },
  { name: 'Telefone', description: 'Telefone do contato', sample: '+5511999999999' },
  { name: 'Empresa', description: 'Nome da empresa', sample: 'Empresa ABC' },
  { name: 'CpfCnpj', description: 'CPF ou CNPJ', sample: '123.456.789-00' },
  { name: 'Email', description: 'Email do contato', sample: 'joao@email.com' },
] as const;

type VariableName = (typeof TEMPLATE_VARIABLES)[number]['name'];

interface TemplateBodyAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  minHeight?: string;
}

export function TemplateBodyAutocomplete({
  value,
  onChange,
  placeholder = 'Digite sua mensagem...',
  className,
  id,
  minHeight = '120px',
}: TemplateBodyAutocompleteProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Insert variable at current cursor position
  const insertVariable = useCallback(
    (varName: VariableName) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const cursorPos = textarea.selectionStart;
      const beforeCursor = value.substring(0, cursorPos);
      const afterCursor = value.substring(cursorPos);

      // Insert the full variable
      const variableText = `{{${varName}}}`;
      const newValue = `${beforeCursor}${variableText}${afterCursor}`;
      onChange(newValue);

      // Move cursor after the inserted variable
      const newCursorPos = cursorPos + variableText.length;
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    [value, onChange]
  );

  return (
    <div className="space-y-2">
      <Textarea
        ref={textareaRef}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(className)}
        style={{ minHeight }}
      />

      {/* Variable buttons */}
      <div className="flex flex-wrap gap-1.5">
        <span className="text-xs text-muted-foreground mr-1 self-center">Variaveis:</span>
        {TEMPLATE_VARIABLES.map((variable) => (
          <Button
            key={variable.name}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs font-mono border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
            onClick={() => insertVariable(variable.name)}
            title={variable.description}
          >
            {`{{${variable.name}}}`}
          </Button>
        ))}
      </div>
    </div>
  );
}

// Helper to get sample values for preview
export function getTemplateSampleValues(): Record<string, string> {
  return TEMPLATE_VARIABLES.reduce(
    (acc, v) => {
      acc[v.name] = v.sample;
      return acc;
    },
    {} as Record<string, string>
  );
}

// Helper to substitute variables in template text with sample values
export function substituteTemplateVariables(
  text: string,
  values?: Record<string, string>
): string {
  const sampleValues = values || getTemplateSampleValues();
  let result = text;

  // Replace named variables like {{Nome}}, {{Empresa}}, etc.
  Object.entries(sampleValues).forEach(([name, value]) => {
    const regex = new RegExp(`\\{\\{${name}\\}\\}`, 'gi');
    result = result.replace(regex, value);
  });

  return result;
}

// Helper to detect variables in template text
export function detectTemplateVariables(text: string): string[] {
  const matches = text.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, '')))];
}

/**
 * Converts named template variables to numbered placeholders for WhatsApp API.
 *
 * WhatsApp Cloud API requires body variables to be numbered ({{1}}, {{2}}, etc.)
 * but we allow users to type named variables ({{Nome}}, {{Empresa}}) for UX.
 *
 * This function:
 * 1. Finds all named variables in order of appearance
 * 2. Replaces them with numbered placeholders
 * 3. Returns the sample values array for the bodyExamples field
 * 4. Returns the variable names array for mapping storage
 *
 * @example
 * Input:  "Ola {{Nome}}, sua empresa {{Empresa}} esta confirmada!"
 * Output: {
 *   convertedText: "Ola {{1}}, sua empresa {{2}} esta confirmada!",
 *   bodyExamples: ["Joao Silva", "Empresa ABC"],
 *   variableNames: ["Nome", "Empresa"]
 * }
 */
/**
 * Converts numbered placeholders back to named variables using a variable mapping.
 * This is used when loading a template from Meta to display user-friendly variable names.
 *
 * @example
 * Input:  text="Ola {{1}}, sua empresa {{2}} esta confirmada!", mapping=["Nome", "Empresa"]
 * Output: "Ola {{Nome}}, sua empresa {{Empresa}} esta confirmada!"
 */
export function convertNumberedVariablesToNamed(
  text: string,
  variableMapping: string[]
): string {
  if (!variableMapping || variableMapping.length === 0) {
    return text;
  }

  let result = text;
  variableMapping.forEach((varName, index) => {
    const placeholderNumber = index + 1;
    const regex = new RegExp(`\\{\\{${placeholderNumber}\\}\\}`, 'g');
    result = result.replace(regex, `{{${varName}}}`);
  });

  return result;
}

/**
 * Substitutes numbered placeholders with sample values using a variable mapping.
 * Used for preview when the text has {{1}}, {{2}} format from Meta.
 *
 * @example
 * Input:  text="Ola {{1}}, empresa {{2}}", mapping=["Nome", "Empresa"]
 * Output: "Ola Joao Silva, empresa Empresa ABC"
 */
export function substituteNumberedVariables(
  text: string,
  variableMapping: string[]
): string {
  if (!variableMapping || variableMapping.length === 0) {
    return text;
  }

  const sampleValues = getTemplateSampleValues();
  let result = text;

  variableMapping.forEach((varName, index) => {
    const placeholderNumber = index + 1;
    const regex = new RegExp(`\\{\\{${placeholderNumber}\\}\\}`, 'g');
    const sampleValue = sampleValues[varName] || `[${varName}]`;
    result = result.replace(regex, sampleValue);
  });

  return result;
}

export function convertNamedVariablesToNumbered(text: string): {
  convertedText: string;
  bodyExamples: string[];
  variableNames: string[];
} {
  const sampleValues = getTemplateSampleValues();

  // Find all named variable matches in order of appearance (preserving duplicates for replacement)
  const allMatches: string[] = [];
  const regex = /\{\{(\w+)\}\}/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    allMatches.push(match[1]);
  }

  // Get unique variable names in order of first appearance
  const uniqueVariables: string[] = [];
  const seen = new Set<string>();
  for (const varName of allMatches) {
    const normalized = varName.toLowerCase();
    if (!seen.has(normalized)) {
      seen.add(normalized);
      uniqueVariables.push(varName);
    }
  }

  // Build the converted text and examples array
  let convertedText = text;
  const bodyExamples: string[] = [];
  const variableNames: string[] = [];

  uniqueVariables.forEach((varName, index) => {
    const placeholderNumber = index + 1;
    // Replace all occurrences of this variable (case-insensitive) with numbered placeholder
    const varRegex = new RegExp(`\\{\\{${varName}\\}\\}`, 'gi');
    convertedText = convertedText.replace(varRegex, `{{${placeholderNumber}}}`);

    // Find the sample value for this variable (case-insensitive match)
    const knownVariable = TEMPLATE_VARIABLES.find(
      (v) => v.name.toLowerCase() === varName.toLowerCase()
    );
    bodyExamples.push(knownVariable?.sample || `[${varName}]`);

    // Store the canonical variable name (use known variable name if found, otherwise original)
    variableNames.push(knownVariable?.name || varName);
  });

  return { convertedText, bodyExamples, variableNames };
}

export { TEMPLATE_VARIABLES };
